import { useEffect, useMemo, useRef, useState } from "react";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import dayjs from "dayjs";
import { useParams } from "react-router";
import { createRow, MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, Chip, IconButton, Select, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RichTextEditor from "../../RichTextEditor/RichTextEditor";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";

const FuelOilEnginesOrOtherFuelConsumerType = ({ enginesOrOtherFuelConsumerData, preFilldata, refetchMonitoringData, vesselMstId }) => {

    const [activeAction, setactiveAction] = useState(-1);
    const [validationErrors, setValidationErrors] = useState({});
    const [fuelConsData, setFuelConsData] = useState([]);
    const [error, setError] = useState("");
    const editorRef = useRef();
    const RowActions = {
        Edit: 0,
        Add: 1,
    };
    useEffect(() => {
        if (Array.isArray(enginesOrOtherFuelConsumerData) && enginesOrOtherFuelConsumerData.length > 0) {
            const transformed = enginesOrOtherFuelConsumerData.map((item) => ({
                enginesOrOtherFuelOilConsumerType: item.enginesOrOtherFuelOilConsumerType || "",
                method: item.method || "",
                description: item.description || "",
            }));
            setFuelConsData(transformed);

        } else if (Array.isArray(preFilldata?.engineFuelOilConsumerTypeData) && preFilldata?.engineFuelOilConsumerTypeData.length > 0) {
            const transformed = preFilldata?.engineFuelOilConsumerTypeData.map((item) => ({
                enginesOrOtherFuelOilConsumerType: item.enginesOrOtherFuelOilConsumerType || "",
                method: item.method || "",
                description: item.description || "",
            }));
            setFuelConsData(transformed);

        } else {
            setFuelConsData([
                {
                    enginesOrOtherFuelOilConsumerType: "",
                    method: "",
                    description: "",
                }
            ]);
        }
    }, [enginesOrOtherFuelConsumerData, preFilldata]);

    const resetActiveRow = () => {
        setactiveAction(-1);
        setValidationErrors({});
    };

    const consumerMonitoringMethodOptions = useMemo(() => {
        return (
            preFilldata?.consumerMonitoringMethodMst?.map((type) => ({
                label: type.value,
                value: type.value,
                id: type.consumerMonitoringMethodMstId
            })) || []
        );
    }, [preFilldata?.consumerMonitoringMethodMst]);

    const typeLabelByCode = useMemo(
        () => Object.fromEntries(consumerMonitoringMethodOptions.map(o => [o.value, o.label])),
        [consumerMonitoringMethodOptions],
    );

    const handleUpdateFuelConsCollectionPlan = async ({ values, table, row }) => {

        const editorContent = editorRef.current?.getEditor()?.root.innerHTML || values.description;
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = editorContent;
        const plainText = tempDiv.textContent?.trim() || "";
        const hasImage = tempDiv.querySelector("img") !== null;
        if (!plainText && !hasImage) {
            setError("Field is required");
            return;
        }
        const updated = fuelConsData.map((item, index) => {
            if (index === row.index) {

                return {
                    ...item,
                    enginesOrOtherFuelOilConsumerType: values?.enginesOrOtherFuelOilConsumerType,
                    method: values?.method,
                    description: editorContent
                };
            }
            return item;
        });
        const finalPayload = (updated).map(i => ({ ...i, index: 0 }));

        try {
            const fuelConsumptionData = await callApi({
                method: "POST",
                url: API_ROUTES.EngineFuelOilConsumerType(vesselMstId),
                data: finalPayload,
            });
            if (fuelConsumptionData?.isSuccess) {
                emitToasterMsg("success", "Table  Method to measure fuel oil consumption", "has been updated successfully");
                table.setEditingRow(null);
                setactiveAction(-1);
                setFuelConsData(finalPayload);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table  Method to measure fuel oil consumption", fuelConsumptionData?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table  Method to measure fuel oil consumption", error.errorMessage);
        }
    };

    const handleReviewUpdateActions = (table, row, action) => {
        if (activeAction !== -1) return;

        if (action === RowActions.Add) {
            const defaultRow = createRow(table, {
                dateOfRevision: dayjs().toISOString(),
                revisedProvision: ''
            });
            table.setCreatingRow(defaultRow);
            setactiveAction(action);
            return;
        }
        if (action === RowActions.Edit) {
            table.setEditingRow(row);
            setactiveAction(action);
            setError("");
        }
    };
    const columns = useMemo(
        () => [
            {
                accessorKey: "enginesOrOtherFuelOilConsumerType",
                header: "Engine or other fuel oil consumer type",
                enableEditing: false,
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle"
                    },
                },
                muiEditTextFieldProps: {
                    inputProps: {
                        maxLength: 255,
                    },
                },
                Cell: ({ cell }) => (
                    <Tooltip
                        title={
                            <span>
                                Value auto-filled. To update, please modify the details in{" "}
                                <a
                                    href={`/my-fleet/enroll-vessel/${vesselMstId}`}
                                   style={{ color: "blue", textDecoration: "underline" }}
                                >
                                    MyFleet
                                </a>
                            </span>
                        }
                        placement="bottom-start"
                        arrow
                    >
                        <span>{cell.getValue()}</span>
                    </Tooltip>
                ),
            },
            {
                accessorKey: "method",
                header: "Method",
                editVariant: 'select',
                editSelectOptions: consumerMonitoringMethodOptions.map(opt => opt.value),
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    const option = consumerMonitoringMethodOptions.find(opt => opt.value === value);
                    return option ? option.label : value;
                },
                size: 220,
                muiEditTextFieldProps: ({ row }) => ({
                    required: true,
                    SelectProps: {
                        MenuProps: {
                            disableScrollLock: true,
                            PaperProps: {
                                sx: {
                                    maxHeight: 250,
                                    boxShadow: 3,
                                    maxWidth: 180,
                                    overflowY: "auto",
                                    '&::-webkit-scrollbar': {
                                        width: "8px"
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'darkgray',
                                        borderRadius: '3px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: '#f0f0f0',
                                    },
                                },
                            },
                            anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                            },
                            transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                            },
                            getContentAnchorEl: null,
                        }
                    }
                }),
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle"
                    },
                },

            },

            {
                accessorKey: "description",
                header: "Description",
                size: 500,
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle"
                    },
                },
                Cell: ({ cell }) => (
                    <div
                        dangerouslySetInnerHTML={{ __html: cell.getValue() }}
                        style={{ width: '100%' }}
                    />
                ),
                Edit: ({ cell, column, row, table }) => (
                    <div style={{ width: "100%" }}>
                        <RichTextEditor
                            ref={editorRef}
                            value={row.original.description || ""}
                            onChange={(value) => {
                                row._valuesCache[column.id] = value;
                                table.setEditingRow(row);
                                setError("");
                            }}
                            style={{ height: '100%' }}
                        />
                        {error && (
                            <div style={{ color: "red", fontSize: "0.9rem", marginTop: "0.25rem" }}>
                                {error}
                            </div>
                        )}
                    </div>
                ),
                muiEditTextFieldProps: {
                    multiline: true,
                    minRows: 1,
                    maxRows: 10,
                    sx: {
                        width: '100%',
                        '& textarea': {
                            resize: 'vertical'
                        }
                    },
                    inputProps: {
                        maxLength: 1000,
                    },

                },
            },
        ], [consumerMonitoringMethodOptions, vesselMstId, error]
    )

    const table = useMaterialReactTable({
        columns,
        data: fuelConsData || [],
        enableStickyHeader: true,
        enableSorting: false,
        enableRowSelection: false,
        enableColumnPinning: false,
        enableTopToolbar: false,
        enableBottomToolbar: false,
        enableColumnActions: false,
        createDisplayMode: "row",
        editDisplayMode: "row",
        enableEditing: true,
        enablePagination: false,
        muiTableHeadCellProps: {
            sx: {
                color: "rgb(255 255 255)",
                backgroundColor: "#042a4c",
                position: "sticky",
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: "520px",
            },
        },
        displayColumnDefOptions: {
            'mrt-row-actions': {
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
            },
        },
        muiTableBodyCellProps: ({ row, cell }) => {
            return {
                sx: {
                    borderRight: "1px solid lightgray",
                    "&:last-child": {
                        borderRight: "none",
                    },
                    color: "",
                },
            };
        },
        muiTableBodyRowProps: ({ row }) => ({
            sx: {
                "&:not(:last-of-type)": {
                    borderBottom: "1px solid lightgray",
                },
                verticalAlign: "top",
            },
        }),
        muiTableHeadRowProps: {
            sx: {
                borderBottom: "2px solid lightgray",
            },
        },
        columnOrder: [
            "enginesOrOtherFuelOilConsumerType",
            "method",
            "description",
        ],
        onEditingRowCancel: resetActiveRow,
        onCreatingRowCancel: resetActiveRow,
        onEditingRowSave: handleUpdateFuelConsCollectionPlan,
        renderToolbarInternalActions: ({ table }) => <></>,
        renderRowActions: ({ row, table }) => {
            return (
                <Box sx={{ display: "flex", gap: "2rem" }}>
                    <Box>
                        <Tooltip title="Edit" sx>
                            <IconButton
                                style={{ padding: "0px" }}
                                onClick={() =>
                                    handleReviewUpdateActions(
                                        table,
                                        row,
                                        RowActions.Edit
                                    )
                                }
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                </Box>
            );
        },

    });

    return (

        <div className="fuel-registry-b3 mt-1">
            <div style={{ marginBottom: '1rem', fontSize: '16px' }}>
                ii.The applied methods for measurement for each consumer type of this ship are given below. The description explains the procedure for measuring data and calculating annual values, measurement equipment involved, etc.

            </div>
            <MaterialReactTable table={table} />
        </div>
    );

}

export default FuelOilEnginesOrOtherFuelConsumerType;